import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo, UserTypes } from "../../../../shared/types/user-info";
import { isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import { compare } from "../../../../shared/utils/values";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { Partner } from "../../partners/partner.type";
import { UserService } from "../user.service";
import { ActivateUser, User } from "../user.type";
import { ActivateUserService } from "./activate-user.service";

@Component({
  selector: "app-activate-user",
  templateUrl: "./activate-user.component.html",
  styleUrls: ["./activate-user.component.css"],
  providers: [ActivateUserService],
})
export class ActivateUserComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  loading = false;
  data: User;
  companies: Company[];
  partners: Partner[];
  currentUser: UserInfo;
  loading$ = this.loader.loading$;
  questions: QuestionBase<string | boolean>[];
  public isRequiredField = isRequiredField;

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<DeleteModalComponent>,
    private loader: LoadingService,
    private userService: UserService,
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private router: Router,
    public location: Location,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private questionsService: ActivateUserService,
  ) {
    this.loader.show();
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  ngOnInit(): void {
    this.getUser(this.route.snapshot.paramMap.get("id"));
  }

  goBack(): void {
    this.location.back();
  }

  onChange(event: AnyObject): void {
    const field: string = event.source?.id;
    const value = event.value;

    // show or hide partner and company fields
    // TODO: when replacing the question, the callback of the replaced question is executed. We need to prevent it.
    if (field === "type") {
      this.dynamicForm.replaceOrAppendQuestion(
        this.questionsService.createPartnerDropdown({
          hide: !(value === UserTypes.Partner || value === UserTypes.Company),
        }),
      );

      this.dynamicForm.replaceOrAppendQuestion(
        this.questionsService.createCompanyDropdown({
          hide: value !== UserTypes.Company,
        }),
      );
    }
  }

  getUser(userId: string): void {
    this.userService
      .getUser(userId)
      .subscribe((user: User) => {
        this.data = user;
        if (this.data.activated) {
          this.router.navigate([`/users/${this.data.id}`]);
        }
        this.questionsService.generateQuestions(this.data).subscribe((qs) => {
          this.questions = qs;
        });
      })
      .add(() => this.loader.hide());
  }

  getCompanies(filter = {}): void {
    const options = {
      ...filter,
      pageSize: 1000,
    };
    this.companyService.getCompanies({ ...options, skipFilter: true }).subscribe((companies) => {
      this.companies = companies.items.sort((a, b) => compare(a.name, b.name));
    });
  }

  activateUser(data: ActivateUser): void {
    this.loader.show();
    this.userService
      .activateUser(this.data.id, data)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (user: User) => {
          this.data = user;
          this.notificationService.success("User activated");
          this.router.navigate([`/users/${this.data.id}`]);
        },
        (error) => {
          this.notificationService.error(`Could not activate user: ${error}`);
        },
      )
      .add(() => this.loader.hide());
  }

  deleteUserModal(): void {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "user",
        data: this.data,
        url: this.userService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/users"]);
      }
    });
  }

  onSubmit($event: any): void {
    const data = {
      type: $event.type,
      right: $event.right,
      partnerId: $event.type === UserTypes.Partner ? $event.partnerId : null,
      companyId: $event.type === UserTypes.Company ? $event.companyId : null,
    };
    const filtered: ActivateUser = Object.keys(data).reduce(
      (acc, curr) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions,no-unused-expressions
        data[curr] ? (acc[curr] = data[curr]) : null;
        return acc;
      },
      { right: null, type: null },
    );
    this.activateUser(filtered);
  }
}
